import type { Account } from '@shared/products-types';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { useNotifications } from '@shared/snackbar';
import { t } from '@luna/i18n';
import { CustomizationService } from '@api-client/products';
import { useActiveProduct } from '@shared/products-core';

import { useCustomizationSuccessHandler } from './useCustomizationSuccessHandler';

const SHOW_ON_OVERVIEW_TRACKING = 'overview_show';
const HIDE_ON_OVERVIEW_TRACKING = 'overview_hide';

const SHOW_ON_OVERVIEW = false;
const HIDE_ON_OVERVIEW = true;

type OverviewAppearanceChangeHook = () => {
    showAccountOnOverview: () => void;
    hideAccountOnOverview: () => void;
};
export const useDashboardAppearanceChange: OverviewAppearanceChangeHook = () => {
    const handleSuccessfulCustomization = useCustomizationSuccessHandler();
    const { id, card, type } = useActiveProduct<Account>();
    const { displaySuccess } = useNotifications('product-settings-details-dashboard-appearance');
    const updateDashboardAppearance = async (hiddenOnOverview: boolean): Promise<void> => {
        CustomizationService.putPublicProductsCustomizationHiddenOnOverview({
            id: card?.id || id,
            requestBody: { hiddenOnOverview },
        }).then((response) => {
            handleSuccessfulCustomization(response);
            georgeStats.trackEvent(
                'details',
                hiddenOnOverview ? HIDE_ON_OVERVIEW_TRACKING : SHOW_ON_OVERVIEW_TRACKING,
            );
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                customisationChanges: ['overviewVisibility'],
                overviewState: hiddenOnOverview ? 'hidden' : 'visible',
                productType: type,
            });

            const changeActionSuccessMessage = hiddenOnOverview
                ? 'products.settings.details.hiddenOnOverview'
                : 'products.settings.details.shownOnOverview';

            displaySuccess(t(changeActionSuccessMessage));
        });
    };
    return {
        showAccountOnOverview: () => updateDashboardAppearance(SHOW_ON_OVERVIEW),
        hideAccountOnOverview: () => updateDashboardAppearance(HIDE_ON_OVERVIEW),
    };
};
